import { type FC, type PropsWithChildren, useContext } from 'react';

import type { QueryClient } from '@tanstack/react-query';

import type { AppConfig } from '@cognite/apm-config';
import { ToastContainer } from '@cognite/cogs-lab-v11';
import { ConfigProvider } from '@cognite/cogs-core-v11/components/config-provider/config-provider.tsx';
import type { AuthStateUser } from '@cognite/e2e-auth';
import type { CogniteClient } from '@cognite/sdk';
import { SDKProvider } from '@cognite/sdk-provider';

import { AtlasHomeOptInProvider } from '../pages/landing-page/hooks/use-has-user-enabled-atlas-home';
import { getRootContainer } from '../pages/utils';

import { CdmAccessProvider } from './access-provider/cdm-access-provider';
import { CdmAppConfigFiltersProvider } from './app-config-filters-provider/cdm-app-config-filters-provider';
import { BackButtonUrlProvider } from './back-button-url-provider/back-button-url-provider';
import type { CdmProvidersContextInterface } from './cdm-providers.context';
import { CdmProvidersContext } from './cdm-providers.context';
import { CdmCurrentUserProvider } from './current-user-provider/cdm-current-user-provider';
import { CdmFDMServicesProvider } from './fdm-services/cdm-fdm-services-provider';
import { CdmAppConfigProvider } from './is-idm-provider/cdm-app-config-provider';
import { NetworkStatusProvider } from './network-status-provider/network-status-provider';
import { PersistQueryFunctionsProvider } from './persist-query-functions-provider/persist-query-functions-provider';
import { CdmSelectedLocationProvider } from './selected-location-provider/cdm-selected-location-provider';
import { CdmUserOnboardingProvider } from './user-onboarding-provider/cdm-user-onboarding-provider';

const cdmProvidersContextDefaultValue: CdmProvidersContextInterface = {
  CdmAppConfigProvider,
  ConfigProvider,
  ToastContainer,
  CdmFDMServicesProvider,
  BackButtonUrlProvider,
  NetworkStatusProvider,
  CdmCurrentUserProvider,
  CdmSelectedLocationProvider,
  CdmUserOnboardingProvider,
  CdmAppConfigFiltersProvider,
  PersistQueryFunctionsProvider,
  SDKProvider,
  CdmAccessProvider,
  AtlasHomeOptInProvider,
};

type CdmProvidersProps = PropsWithChildren<{
  i18nLanguage?: string;
  authClient: CogniteClient;
  queryClient: QueryClient;
  fdmServiceProviderMock?: {
    appConfig: AppConfig;
    client?: CogniteClient;
    user?: AuthStateUser;
  };
  userMock?: AuthStateUser;
}>;

export const CdmProviders: FC<CdmProvidersProps> = ({
  children,
  i18nLanguage,
  authClient,
  queryClient,
}) => {
  const {
    CdmAppConfigProvider,
    ConfigProvider,
    ToastContainer,
    CdmFDMServicesProvider,
    BackButtonUrlProvider,
    NetworkStatusProvider,
    CdmCurrentUserProvider,
    CdmUserOnboardingProvider,
    CdmAppConfigFiltersProvider,
    PersistQueryFunctionsProvider,
    SDKProvider,
    CdmAccessProvider,
    CdmSelectedLocationProvider,
    AtlasHomeOptInProvider,
  } = useContext(CdmProvidersContext) ?? cdmProvidersContextDefaultValue;

  return (
    <CdmCurrentUserProvider>
      <CdmSelectedLocationProvider>
        <CdmUserOnboardingProvider>
          <CdmAppConfigProvider>
            <ConfigProvider
              localizationProviderProps={{
                adapterLocale: i18nLanguage,
              }}
              getOverlayContainer={getRootContainer}
            >
              <>
                <ToastContainer />
                <CdmFDMServicesProvider>
                  <BackButtonUrlProvider>
                    <NetworkStatusProvider>
                      <CdmAppConfigFiltersProvider>
                        <PersistQueryFunctionsProvider client={queryClient}>
                          <SDKProvider sdk={authClient}>
                            <CdmAccessProvider>
                              <AtlasHomeOptInProvider>
                                {children}
                              </AtlasHomeOptInProvider>
                            </CdmAccessProvider>
                          </SDKProvider>
                        </PersistQueryFunctionsProvider>
                      </CdmAppConfigFiltersProvider>
                    </NetworkStatusProvider>
                  </BackButtonUrlProvider>
                </CdmFDMServicesProvider>
              </>
            </ConfigProvider>
          </CdmAppConfigProvider>
        </CdmUserOnboardingProvider>
      </CdmSelectedLocationProvider>
    </CdmCurrentUserProvider>
  );
};
